/**
 * Visitor identity.
 *
 * The anonymous id is a random value in `localStorage` — it identifies a
 * browser, never a person, and is scoped per project so the same browser
 * visiting two customers' sites is two unrelated visitors. The session id
 * lives in `sessionStorage` so a new tab naturally starts a new session.
 *
 * Storage can throw (Safari private mode, blocked third-party storage), so
 * every access degrades to an in-memory value rather than breaking the widget.
 */

const memoryFallback = new Map<string, string>();

function storageKey(projectKey: string, name: string): string {
  return `intent:${name}:${projectKey}`;
}

function readStorage(storage: Storage | null, key: string): string | null {
  if (!storage) return memoryFallback.get(key) ?? null;
  try {
    return storage.getItem(key);
  } catch {
    return memoryFallback.get(key) ?? null;
  }
}

function writeStorage(storage: Storage | null, key: string, value: string): void {
  memoryFallback.set(key, value);
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Nothing to do: the in-memory fallback already holds the value.
  }
}

function safeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/** RFC 4122 v4 id, falling back for browsers without `crypto.randomUUID`. */
export function generateId(): string {
  const cryptoObj = window.crypto;

  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }

  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return `fallback-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export class IdentityStore {
  private readonly local = safeStorage('local');
  private readonly session = safeStorage('session');

  constructor(private readonly projectKey: string) {}

  /** Stable across visits. Created on first ever page load. */
  getAnonymousId(): string {
    const key = storageKey(this.projectKey, 'anon');
    const existing = readStorage(this.local, key);
    if (existing) return existing;

    const created = generateId();
    writeStorage(this.local, key, created);
    return created;
  }

  /** Server-assigned; cleared when the tab closes. */
  getSessionId(): string | null {
    return readStorage(this.session, storageKey(this.projectKey, 'sid'));
  }

  setSessionId(sessionId: string): void {
    writeStorage(this.session, storageKey(this.projectKey, 'sid'), sessionId);
  }

  setAnonymousId(anonymousId: string): void {
    writeStorage(this.local, storageKey(this.projectKey, 'anon'), anonymousId);
  }
}
